import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  FileText,
  Clock,
  AlertCircle,
  Upload,
  Users,
  Building2,
  ArrowRight,
  FileCheck,
  FileWarning,
  Loader2,
  TrendingUp,
  BarChart3,
  Activity,
  Cpu,
  FileSignature,
  AlertTriangle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const STATUS_COLORS = {
  pending: '#94a3b8',
  processing: '#38bdf8',
  ocr_complete: '#22d3ee',
  needs_review: '#f59e0b',
  reviewed: '#818cf8',
  approved: '#10b981',
  rejected: '#ef4444',
};

const PIE_COLORS = ['#4F46E5', '#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [moduleStats, setModuleStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [ocrQueue, setOCRQueue] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState('7');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  useEffect(() => {
    if (user?.tenant_id) {
      dashboardAPI.getAnalytics(user.tenant_id, parseInt(analyticsDays)).then((res) => setAnalytics(res.data));
    }
  }, [user, analyticsDays]);

  const loadDashboardData = async () => {
    try {
      const tenantId = user?.tenant_id;
      const [statsRes, moduleRes, activityRes, analyticsRes, ocrRes] = await Promise.all([
        dashboardAPI.getStats(tenantId),
        tenantId ? dashboardAPI.getModuleStats(tenantId) : Promise.resolve({ data: null }),
        dashboardAPI.getRecentActivity(tenantId, 10),
        tenantId ? dashboardAPI.getAnalytics(tenantId, 7) : Promise.resolve({ data: null }),
        tenantId ? dashboardAPI.getOCRQueueStats(tenantId) : Promise.resolve({ data: null }),
      ]);

      setStats(statsRes.data);
      setModuleStats(moduleRes.data);
      setRecentActivity(activityRes.data);
      setAnalytics(analyticsRes.data);
      setOCRQueue(ocrRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Documents Today', value: stats?.documents?.uploaded_today || 0, icon: Upload, color: 'text-blue-600', bg: 'bg-blue-50' },
    { title: 'Total Documents', value: stats?.documents?.total || 0, icon: FileText, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { title: 'Pending Review', value: stats?.documents?.pending_review || 0, icon: FileWarning, color: 'text-amber-600', bg: 'bg-amber-50', link: '/review-queue' },
    { title: 'Processing', value: stats?.documents?.processing || 0, icon: Loader2, color: 'text-cyan-600', bg: 'bg-cyan-50' },
  ];

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pending', variant: 'secondary' },
      processing: { label: 'Processing', variant: 'default' },
      ocr_complete: { label: 'OCR Complete', variant: 'outline' },
      needs_review: { label: 'Needs Review', variant: 'warning' },
      reviewed: { label: 'Reviewed', variant: 'default' },
      approved: { label: 'Approved', variant: 'success' },
    };
    const config = statusMap[status] || { label: status, variant: 'secondary' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Prepare chart data
  const statusChartData = analytics?.documents_by_status
    ? Object.entries(analytics.documents_by_status).map(([name, value]) => ({ name: name.replaceAll('_', ' '), value }))
    : [];

  const moduleChartData = analytics?.documents_by_module
    ? Object.entries(analytics.documents_by_module).map(([name, value]) => ({ name: name.replaceAll('_', ' '), value }))
    : [];

  const confidenceData = analytics?.confidence_by_type || [];

  const ocrStatusData = ocrQueue?.status_counts
    ? Object.entries(ocrQueue.status_counts).map(([name, value]) => ({ name: name.replaceAll('_', ' '), value }))
    : [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div><Skeleton className="h-8 w-48" /><Skeleton className="h-4 w-64 mt-2" /></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-72" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">
            Welcome back, {user?.first_name}
          </h1>
          <p className="text-slate-500 mt-1">Here's what's happening with your documents today.</p>
        </div>
        <Button asChild>
          <Link to="/documents" data-testid="upload-doc-btn">
            <Upload className="mr-2 h-4 w-4" />
            Upload Document
          </Link>
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={stat.title} className="card-hover animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className={`p-3 rounded-lg ${stat.bg}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
                {stat.link && stat.value > 0 && (
                  <Link to={stat.link}>
                    <Button variant="ghost" size="sm">View <ArrowRight className="ml-1 h-4 w-4" /></Button>
                  </Link>
                )}
              </div>
              <div className="mt-4">
                <p className="text-3xl font-semibold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500 mt-1">{stat.title}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Documents Trend */}
        <Card data-testid="analytics-documents-trend">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-lg">Document Upload Trend</CardTitle>
              </div>
              <Select value={analyticsDays} onValueChange={setAnalyticsDays}>
                <SelectTrigger className="w-[120px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {analytics?.documents_by_day?.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={analytics.documents_by_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => format(new Date(d + 'T00:00'), 'MMM d')} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip labelFormatter={(d) => format(new Date(d + 'T00:00'), 'MMM d, yyyy')} />
                  <Line type="monotone" dataKey="count" stroke="#4F46E5" strokeWidth={2} dot={{ fill: '#4F46E5', r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[240px] flex items-center justify-center text-sm text-slate-400">No data for this period</div>
            )}
          </CardContent>
        </Card>

        {/* Status Breakdown */}
        <Card data-testid="analytics-status-breakdown">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-indigo-600" />
              <CardTitle className="text-lg">Document Status Breakdown</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {statusChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={statusChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {statusChartData.map((entry, index) => {
                      const key = entry.name.replaceAll(' ', '_');
                      return <Cell key={index} fill={STATUS_COLORS[key] || PIE_COLORS[index % PIE_COLORS.length]} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[240px] flex items-center justify-center text-sm text-slate-400">No documents yet</div>
            )}
          </CardContent>
        </Card>

        {/* OCR Queue Health */}
        <Card data-testid="analytics-ocr-queue">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Cpu className="h-5 w-5 text-indigo-600" />
              <CardTitle className="text-lg">OCR Queue Health</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-6">
              {ocrStatusData.length > 0 ? (
                <div className="flex-1">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={ocrStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                        {ocrStatusData.map((_, index) => (
                          <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex-1 h-[200px] flex items-center justify-center text-sm text-slate-400">No OCR jobs yet</div>
              )}
              <div className="space-y-3 min-w-[160px]">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xl font-semibold text-slate-900">{Math.round(ocrQueue?.avg_processing_time_ms || 0)}ms</p>
                  <p className="text-xs text-slate-500">Avg Processing Time</p>
                </div>
                {ocrQueue?.recent_failures?.length > 0 && (
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-sm font-semibold text-red-700">{ocrQueue.recent_failures.length} Failed</p>
                    <p className="text-xs text-red-500 truncate mt-0.5">{ocrQueue.recent_failures[0]?.error_message?.slice(0, 50)}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Confidence by Document Type */}
        <Card data-testid="analytics-confidence">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-indigo-600" />
              <CardTitle className="text-lg">OCR Confidence by Type</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {confidenceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={confidenceData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" domain={[0, 1]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} />
                  <YAxis dataKey="document_type" type="category" tick={{ fontSize: 11 }} width={80} />
                  <Tooltip formatter={(v) => `${(v * 100).toFixed(1)}%`} />
                  <Bar dataKey="avg_confidence" radius={[0, 4, 4, 0]} fill="#4F46E5">
                    {confidenceData.map((entry, index) => (
                      <Cell key={index} fill={entry.avg_confidence >= 0.85 ? '#10B981' : entry.avg_confidence >= 0.7 ? '#F59E0B' : '#EF4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[240px] flex items-center justify-center text-sm text-slate-400">No OCR data available</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Module Stats */}
      {moduleStats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Timesheet Module */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-50">
                    <Clock className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Timesheet Module</CardTitle>
                    <CardDescription>Employee time tracking</CardDescription>
                  </div>
                </div>
                <Link to="/timesheet">
                  <Button variant="outline" size="sm" data-testid="view-timesheets-btn">View All</Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-semibold text-slate-900">{moduleStats.timesheet?.total_entries || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Total Entries</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-semibold text-amber-700">{moduleStats.timesheet?.pending_review || 0}</p>
                  <p className="text-xs text-amber-600 mt-1">Pending Review</p>
                </div>
                <div className="text-center p-4 bg-indigo-50 rounded-lg">
                  <p className="text-2xl font-semibold text-indigo-700">{moduleStats.timesheet?.hours_this_week || 0}</p>
                  <p className="text-xs text-indigo-600 mt-1">Hours This Week</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Client Intake Module */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-50">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Client Intake</CardTitle>
                    <CardDescription>Receipt & document collection</CardDescription>
                  </div>
                </div>
                <Link to="/intake">
                  <Button variant="outline" size="sm" data-testid="view-intake-btn">View All</Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-semibold text-slate-900">{moduleStats.intake?.total_clients || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Total Clients</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-semibold text-slate-900">{moduleStats.intake?.total_receipts || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Total Receipts</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-semibold text-amber-700">{moduleStats.intake?.pending_review || 0}</p>
                  <p className="text-xs text-amber-600 mt-1">Pending Review</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Contracts Expiration Widget */}
      {stats?.contracts && (stats.contracts.expiring_90 > 0 || stats.contracts.expiring_180 > 0 || stats.contracts.expired > 0) && (
        <Card className="border-l-4 border-l-amber-400" data-testid="contracts-alert-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileSignature className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-lg">Contract Alerts</CardTitle>
              </div>
              <Link to="/contracts">
                <Button variant="outline" size="sm" data-testid="view-contracts-btn">View All</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-4 bg-emerald-50 rounded-lg">
                <p className="text-2xl font-semibold text-emerald-700">{stats.contracts.active}</p>
                <p className="text-xs text-emerald-600 mt-1">Active</p>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-lg">
                <p className="text-2xl font-semibold text-amber-700">{stats.contracts.expiring_180}</p>
                <p className="text-xs text-amber-600 mt-1">Warning (180d)</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-2xl font-semibold text-red-700">{stats.contracts.expiring_90}</p>
                <p className="text-xs text-red-600 mt-1">Critical (90d)</p>
              </div>
              <div className="text-center p-4 bg-slate-100 rounded-lg">
                <p className="text-2xl font-semibold text-slate-700">{stats.contracts.expired}</p>
                <p className="text-xs text-slate-500 mt-1">Expired</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Documents */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Recent Documents</CardTitle>
              <CardDescription>Latest uploaded documents</CardDescription>
            </div>
            <Link to="/documents">
              <Button variant="outline" size="sm">View All</Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {recentActivity?.recent_documents?.length > 0 ? (
            <div className="space-y-3">
              {recentActivity.recent_documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-white rounded-lg border border-slate-200">
                      <FileText className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 truncate max-w-[300px]">{doc.original_filename}</p>
                      <p className="text-sm text-slate-500">{doc.module_id} {doc.uploader_name ? `\u2022 ${doc.uploader_name}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {getStatusBadge(doc.status)}
                    <span className="text-sm text-slate-400">
                      {doc.created_at && format(new Date(doc.created_at), 'MMM d, h:mm a')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No documents yet</p>
              <p className="text-sm text-slate-400 mt-1">Upload your first document to get started</p>
              <Button asChild className="mt-4">
                <Link to="/documents"><Upload className="mr-2 h-4 w-4" />Upload Document</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Stats for Super Admin */}
      {user?.role === 'super_admin' && stats?.system && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-purple-50">
                  <Building2 className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-slate-900">{stats.system.tenants}</p>
                  <p className="text-sm text-slate-500">Active Tenants</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-green-50">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-slate-900">{stats.system.users}</p>
                  <p className="text-sm text-slate-500">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
